import { Icon } from '@ant-design/compatible';
import { Avatar, Popover } from 'antd';
import React from 'react';
import { SectionTitle } from '../../components/text/Titles';
import { ruleTranslations } from '../../config/Translations';
import { isReliableAndExitsOrManuallyApproved, isReliableAndNotOnDocument } from '../../lib/util/status';
import useGlobal from '../../store/Store';
import { TableColumn, TableResult } from '../../types';
import { MultiflowValidationResult } from '../../types/flow';
import { Label } from '../../types/label';

interface Props {
  table: TableResult | null;
  maxHeight: number;
  isMultiflow: () => any;
  multiValidationResult: null | MultiflowValidationResult;
}

export const SingleTable = ({ table, maxHeight, isMultiflow, multiValidationResult }: Props) => {
  const [globalState] = useGlobal();
  if (!table) return null;

  const getData = (it: Label) => {
    const data = [
      {
        left: 'Location',
        valid: isReliableAndExitsOrManuallyApproved(it),
        right: isReliableAndExitsOrManuallyApproved(it)
          ? 'Found'
          : isReliableAndNotOnDocument(it)
          ? 'Not on invoice'
          : 'Not reliable',
        color: isReliableAndExitsOrManuallyApproved(it)
          ? '#52c41a'
          : isReliableAndNotOnDocument(it)
          ? '#8c8c8c'
          : '#fa541c',
      },
      ...(isMultiflow() && !(!it.exists && it.reliable)
        ? [
            {
              left: 'Original value',
              valid: true,
              right: `"${it.originalWord || it.word}"`,
              color: '#8c8c8c',
            },
            it.prob
              ? {
                  left: 'Confidence',
                  valid: it.reliable,
                  right: `${Math.round(it.prob * 100).toFixed(0)}%`,
                  color: it.reliable ? '#52c41a' : '#fa541c',
                }
              : {},
          ]
        : []),
      it.type
        ? {
            left: ruleTranslations[`is${it.type.charAt(0).toUpperCase() + it.type.slice(1)}`],
            right: it.valid ? 'OK' : 'Not valid',
            valid: it.valid,
            color: it.valid ? '#52c41a' : '#fa541c',
          }
        : null,
    ];
    const rules = it.rules
      ? it.rules.map((rule: any) => ({
          left: ruleTranslations[rule.name],
          right: rule.result && rule.result === 'valid' ? 'OK' : 'Not valid',
          valid: rule.result && rule.result === 'valid',
          color: rule.result === 'valid' ? '#52c41a' : '#fa541c',
        }))
      : multiValidationResult && multiValidationResult.validation.fields[it.name]
      ? Object.entries(multiValidationResult.validation.fields[it.name].rules).map(([key, value]: any) => ({
          left: value.description,
          right: value.success ? 'OK' : 'Not valid',
          valid: value.success,
          color: value.success ? '#52c41a' : '#fa541c',
        }))
      : [];

    const dataWithRules = [...data.filter(Boolean), ...rules];
    return dataWithRules;
  };

  const popupContent = (dataWithRules: any) => {
    return (
      <>
        {dataWithRules.map((dataRow: any) => (
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 32, padding: 6 }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
              <Avatar
                style={{
                  backgroundColor: 'transparent',
                }}
                size={'small'}
                icon={
                  <Icon
                    type={dataRow.valid ? 'check' : 'exclamation-circle'}
                    style={{
                      color: dataRow.valid ? '#52c41a' : '#fa541c',
                    }}
                  />
                }
              />
              <div>{dataRow.left}</div>
            </div>
            <div style={{ color: dataRow.color }}>{dataRow.right}</div>
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      <div>
        <SectionTitle fontSize={16}>{table.name}</SectionTitle>
      </div>
      <div style={{ maxHeight: maxHeight - 50, overflowY: 'scroll', border: '1px solid #f0f0f0' }}>
        <table style={{ width: 'max-content' }}>
          <thead>
            <tr
              style={{
                background: '#fafafa',
              }}>
              {table.header.map(header => (
                <th
                  style={{
                    writingMode: 'vertical-lr',
                    rotate: '180deg',
                    padding: 10,
                    textAlign: 'left',
                    border: '1px solid #f0f0f0',
                  }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.content.map((tableRow: { [key: string]: TableColumn }, index) => {
              return (
                <tr key={index}>
                  {table.header.map(head => (
                    <td style={{ border: '1px solid #f0f0f0', padding: 6 }}>
                      {!!tableRow[head] ? (
                        <Popover
                          content={
                            <div>
                              {popupContent(
                                getData(globalState.labels.filter(label => label.id === tableRow[head].id)[0])
                              )}
                            </div>
                          }
                          title="Process">
                          {tableRow[head].positions[0].word}
                        </Popover>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};
